/**
 * Configuración → Máquinas (`/configuracion/maquinas`).
 *
 * Puerto de `Maquina` + `Maquina_model` + `views/menu/maquina/*`: el catálogo de
 * surtidores. `tipo_maquina` 1 = **Con contadores** (combustibles) y 2 = **Sin
 * contadores (Bodega)** (aceites).
 *
 * Reglas conservadas: el alta fija el estado en activo y la fecha, la foto es
 * opcional (`images/maquinas/`) y el nombre es obligatorio.
 *
 * Dos diferencias anotadas:
 * - El panel anterior ocultaba las máquinas desactivadas (`estado_maquina = 1`
 *   en el listado) y no había forma de reactivarlas. Aquí se listan todas y se
 *   activan/desactivan desde la propia fila.
 * - No hay borrado (los lados y los contómetros apuntan a la máquina): se
 *   desactiva.
 */

import { ArrowLeft, Fuel, Image as ImageIcon, Pencil, Plus, ShieldAlert, Truck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn } from '@/lib/utils';

import { urlFotoMaquina, useMaquinas, useMutarMaquinas } from '../avanzado.api';
import type { Maquina, MaquinaPayload } from '../avanzado.types';
import { FotoDialogo } from '../components/foto-dialogo';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Miniatura de la foto de una máquina (la mayoría no tiene). */
function Miniatura({ maquina }: { maquina: Maquina }) {
  const [fallo, setFallo] = useState(false);

  if (!maquina.foto || fallo) {
    return (
      <span
        aria-hidden="true"
        className="flex size-8 items-center justify-center rounded-control bg-ink-100 text-ink-400"
      >
        <ImageIcon className="size-4" />
      </span>
    );
  }

  return (
    <img
      src={urlFotoMaquina(maquina.id, maquina.foto)}
      alt={`Foto de ${maquina.nombre}`}
      className="size-8 rounded-control object-cover ring-1 ring-ink-100"
      onError={() => setFallo(true)}
    />
  );
}

/** Alta/edición de una máquina. */
function MaquinaDialogo({
  maquina,
  tipos,
  guardando,
  error,
  onGuardar,
  onCerrar,
}: {
  maquina: Maquina | null;
  tipos: { valor: number; etiqueta: string }[];
  guardando: boolean;
  error: string | null;
  onGuardar: (payload: MaquinaPayload) => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(maquina?.nombre ?? '');
  const [tipo, setTipo] = useState<number>(maquina?.tipo ?? 1);
  const [activo, setActivo] = useState(maquina?.activo ?? true);

  const valido = nombre.trim().length > 0 && tipo > 0;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title={maquina ? `Editar máquina ${maquina.nombre}` : 'Nueva máquina'}
      description="Las máquinas con contadores tienen lados y mangueras; las de bodega sólo sirven para aceites."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            size="sm"
            loading={guardando}
            disabled={!valido}
            onClick={() =>
              onGuardar({ nombre: nombre.trim(), tipo, ...(maquina ? { activo } : {}) })
            }
          >
            Guardar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}

        <Input
          label="Nombre de la máquina"
          value={nombre}
          maxLength={400}
          autoFocus
          placeholder="Ej. MAQ 1"
          onChange={(evento) => setNombre(evento.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="maquina-tipo" className="text-sm font-medium text-ink-700">
            Tipo
          </label>
          <Select
            id="maquina-tipo"
            className={CONTROL}
            value={String(tipo)}
            onChange={(evento) => setTipo(Number(evento.target.value))}
          >
            {tipos.map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {opcion.etiqueta}
              </option>
            ))}
          </Select>
        </div>

        {maquina ? (
          <Checkbox
            label="Máquina activa"
            hint="Una máquina desactivada no aparece en los selectores del sistema"
            checked={activo}
            onChange={(evento) => setActivo(evento.target.checked)}
          />
        ) : null}
      </div>
    </Dialog>
  );
}

/** Cambio de la foto de una máquina (opcional). */

export function MaquinasPage() {
  useDocumentTitle('Configuración · Máquinas');

  const maquinas = useMaquinas();
  const mutaciones = useMutarMaquinas();

  const [dialogo, setDialogo] = useState<{ abierto: boolean; maquina: Maquina | null }>({
    abierto: false,
    maquina: null,
  });
  const [foto, setFoto] = useState<Maquina | null>(null);
  const [porCambiar, setPorCambiar] = useState<Maquina | null>(null);
  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [errorFoto, setErrorFoto] = useState<string | null>(null);

  const sinPermiso = maquinas.error instanceof ApiError && maquinas.error.status === 403;
  const lista = maquinas.data?.maquinas ?? [];
  const tipos = maquinas.data?.tipos ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Fuel className="size-6 text-brand-500" aria-hidden="true" />
          Máquinas
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Surtidores del grifo: cada máquina con contadores tiene lados y mangueras. Las de bodega
          agrupan los aceites.
        </p>
      </div>

      <Button
        size="sm"
        leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
        disabled={sinPermiso}
        onClick={() => {
          setErrorDialogo(null);
          setDialogo({ abierto: true, maquina: null });
        }}
      >
        Nueva máquina
      </Button>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Administrador</strong>, como en el panel anterior.
          </span>
        </Alert>
      </div>
    );
  }

  const guardar = (payload: MaquinaPayload) => {
    setErrorDialogo(null);
    const alGuardar = {
      onSuccess: (datos: { mensaje: string }) => {
        setDialogo({ abierto: false, maquina: null });
        setAviso({ tono: 'success' as const, texto: datos.mensaje });
      },
      onError: (error: unknown) =>
        setErrorDialogo(mensajeDe(error, 'No se pudo guardar la máquina.')),
    };

    if (dialogo.maquina) {
      mutaciones.actualizar.mutate({ id: dialogo.maquina.id, payload }, alGuardar);
      return;
    }

    mutaciones.crear.mutate(payload, alGuardar);
  };

  const cambiarEstado = () => {
    if (!porCambiar) return;

    mutaciones.cambiarEstado.mutate(
      { id: porCambiar.id, activo: !porCambiar.activo },
      {
        onSuccess: (datos) => {
          setPorCambiar(null);
          setAviso({ tono: 'success', texto: datos.mensaje });
        },
        onError: (error) => {
          setPorCambiar(null);
          setAviso({ tono: 'error', texto: mensajeDe(error, 'No se pudo cambiar el estado.') });
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      {maquinas.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : maquinas.error && !sinPermiso ? (
        <Alert variant="error" title="No se pudieron leer las máquinas">
          {mensajeDe(maquinas.error, 'La API no respondió.')}
        </Alert>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Lista de máquinas</CardTitle>
            <Badge variant="neutral">
              {maquinas.data?.activas ?? 0} activas de {maquinas.data?.total ?? 0}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[46rem] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    <th className="w-12 pb-2">Foto</th>
                    <th className="pb-2">Máquina</th>
                    <th className="w-56 pb-2">Tipo</th>
                    <th className="w-24 pb-2 text-center">Lados</th>
                    <th className="w-28 pb-2">Estado</th>
                    <th className="w-32 pb-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((maquina) => (
                    <tr key={maquina.id} className="border-t border-ink-100">
                      <td className="py-3">
                        <Miniatura maquina={maquina} />
                      </td>
                      <td className="py-3 font-medium text-ink-900">{maquina.nombre}</td>
                      <td className="py-3">
                        <Badge variant={maquina.tipo === 1 ? 'brand' : 'outline'}>
                          <Truck className="size-3" aria-hidden="true" />
                          {maquina.tipo_etiqueta}
                        </Badge>
                      </td>
                      <td className="py-3 text-center text-ink-700">{maquina.lados}</td>
                      <td className="py-3">
                        <Badge variant={maquina.activo ? 'success' : 'neutral'}>
                          {maquina.activo ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Foto de ${maquina.nombre}`}
                            title="Cambiar foto"
                            onClick={() => {
                              setErrorFoto(null);
                              setFoto(maquina);
                            }}
                          >
                            <ImageIcon className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Editar ${maquina.nombre}`}
                            title="Editar"
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ abierto: true, maquina });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn('text-xs', maquina.activo ? 'text-ink-500' : 'text-brand-600')}
                            onClick={() => setPorCambiar(maquina)}
                          >
                            {maquina.activo ? 'Desactivar' : 'Activar'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Alert variant="info" className="mt-4">
              Una máquina <strong>no se elimina</strong>: sus lados, mangueras y contómetros
              apuntan a ella. Si ya no se usa, se desactiva.
            </Alert>
          </CardContent>
        </Card>
      )}

      {dialogo.abierto ? (
        <MaquinaDialogo
          key={`maquina-${dialogo.maquina?.id ?? 'nueva'}`}
          maquina={dialogo.maquina}
          tipos={tipos}
          guardando={mutaciones.crear.isPending || mutaciones.actualizar.isPending}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo({ abierto: false, maquina: null })}
        />
      ) : null}

      {foto ? (
        <FotoDialogo
          key={`foto-${foto.id}`}
          titulo={`Foto de ${foto.nombre}`}
          descripcion="Opcional. Se guarda en images/maquinas y reemplaza la anterior."
          urlActual={foto.foto ? urlFotoMaquina(foto.id, foto.foto) : null}
          altActual={foto.nombre}
          subiendo={mutaciones.subirFoto.isPending}
          error={errorFoto}
          onSubir={(archivo) => {
            setErrorFoto(null);
            mutaciones.subirFoto.mutate(
              { id: foto.id, archivo },
              {
                onSuccess: () => {
                  setFoto(null);
                  setAviso({ tono: 'success', texto: 'Foto de la máquina actualizada.' });
                },
                onError: (error) => setErrorFoto(mensajeDe(error, 'No se pudo subir la foto.')),
              },
            );
          }}
          onCerrar={() => setFoto(null)}
        />
      ) : null}

      <ConfirmDialog
        open={porCambiar !== null}
        title={porCambiar?.activo ? '¿Desactivar la máquina?' : '¿Activar la máquina?'}
        description={
          porCambiar?.activo
            ? `«${porCambiar?.nombre ?? ''}» dejará de aparecer en los selectores del sistema. Sus lados y contómetros se conservan.`
            : `«${porCambiar?.nombre ?? ''}» volverá a estar disponible en el sistema.`
        }
        confirmLabel={porCambiar?.activo ? 'Sí, desactivar' : 'Sí, activar'}
        loading={mutaciones.cambiarEstado.isPending}
        onClose={() => setPorCambiar(null)}
        onConfirm={cambiarEstado}
      />
    </div>
  );
}
